import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { X, LayoutDashboard, Loader2, AlertCircle } from 'lucide-react';

export default function GraficoFaturamentoAno({ onClose }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [anoInicio, setAnoInicio] = useState(2023);
    const [anoFinal, setAnoFinal] = useState(2026);
    const [empresaFiltro, setEmpresaFiltro] = useState('TODAS');

    const mesesNomes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const base_url = 'https://onboard.fck.ind.br';

    useEffect(() => {
        const fetchData = async () => {
            // Recupera o token do localStorage
            const token = localStorage.getItem('@user_token');
            
            if (!token) {
                setError("Sessão expirada. Por favor, faça login novamente.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                const params = new URLSearchParams({
                    anoInicio,
                    anoFinal,
                    empresa: empresaFiltro
                });

                console.log(params.toString())

                // Chamada refatorada com Method, Headers e Token
                const res = await fetch(`${base_url}/api/dashboard/Faturamento/grafico?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
        
                if (!res.ok) {
                    if (res.status === 401) throw new Error('Não autorizado. Token inválido.');
                    throw new Error('Erro ao buscar dados do servidor');
                }
                
                const result = await res.json();
                setData(result);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [anoInicio, anoFinal, empresaFiltro, base_url]);

    // Lógica de Processamento e Cores Dinâmicas corrigida para o seu JSON
    const { chartData, anosPresentes } = useMemo(() => {
        // Mapeia os anos presentes nos dados vindos do backend
        const anos = [...new Set(data.map(item => item.ano))].sort();
        
        const monthlyData = Array.from({ length: 12 }, (_, i) => {
            const mesNum = i + 1;
            const ponto = { name: mesesNomes[i] };
            
            anos.forEach(ano => {                
                const registro = data.find(d => d.mês === mesNum && d.ano === ano);               
              
                ponto[`Ano_${ano}`] = registro ? registro.valor_Total : null;
            });
            return ponto;
        });

        return { chartData: monthlyData, anosPresentes: anos };
    }, [data]);

    const getCorAno = (index) => {
        const cores = ["#58a6ff", "#238636", "#d29922", "#f85149", "#8957e5"];
        return cores[index % cores.length];
    };

    const formatBRL = (val) =>
        Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const handleAnoInicio = (val) => {
        const novoInicio = parseInt(val);
        setAnoInicio(novoInicio);
        if (anoFinal - novoInicio > 3) setAnoFinal(novoInicio + 3);
        if (novoInicio > anoFinal) setAnoFinal(novoInicio);
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
            <div className="bg-[#0d1117] border border-gray-800 w-full max-w-6xl h-[85vh] rounded-lg shadow-2xl overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-800 bg-[#161b22]">
                    <div className="flex items-center gap-2 text-blue-400 font-bold tracking-widest text-xs uppercase">
                        <LayoutDashboard size={18} />
                        <span>Comparativo Anual Dinâmico</span>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 bg-[#0d1117] border-b border-gray-800 flex flex-wrap gap-6 items-end">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Ano Início</label>
                        <select value={anoInicio} onChange={e => handleAnoInicio(e.target.value)} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500">
                            {[2022, 2023, 2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Ano Final</label>
                        <select value={anoFinal} onChange={e => setAnoFinal(parseInt(e.target.value))} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500">
                            {[anoInicio, anoInicio + 1, anoInicio + 2, anoInicio + 3, anoInicio + 4].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase font-bold">Empresa</label>
                        <select value={empresaFiltro} onChange={e => setEmpresaFiltro(e.target.value)} className="bg-[#161b22] border border-gray-700 text-gray-300 text-xs rounded p-2 outline-none focus:border-blue-500">
                            <option value="TODAS">TODAS</option>
                            <option value="1">FCK SJL</option>                            
                            <option value="2">FCK CTG</option>
                            <option value="3">FCK BHZ</option>
                        </select>
                    </div>
                </div>

                <div className="flex-1 p-6 bg-[#0d1117] flex items-center justify-center">
                    {loading ? (
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="animate-spin text-blue-500" />
                            <span className="text-gray-500 text-[10px] uppercase font-bold">Buscando do Backend...</span>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center text-red-500 gap-2 text-center">
                            <AlertCircle size={32} />
                            <span className="text-sm font-bold">{error}</span>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#30363d" vertical={false} />
                                <XAxis dataKey="name" stroke="#8b949e" fontSize={11} />
                                <YAxis stroke="#8b949e" fontSize={11} tickFormatter={(val) => `R$ ${(val/1000).toFixed(0)}k`} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#0d1117', border: '1px solid #30363d', borderRadius: '8px' }}
                                    formatter={(value) => [formatBRL(value), "Valor"]}
                                />
                                <Legend />
                                {anosPresentes.map((ano, index) => (
                                    <Line 
                                        key={ano}
                                        type="monotone" 
                                        dataKey={`Ano_${ano}`} 
                                        name={`Ano ${ano}`}
                                        stroke={getCorAno(index)} 
                                        strokeWidth={3}
                                        dot={{ r: 4 }}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
}